import { FilterDefinitionItem } from '../../core/model/filter-definition-item';

/**
 * Restricts the property picker to the companies selected in the same filter.
 * An empty company selection deliberately leaves the property picker unrestricted.
 */
export function updateCompanyDependentPropertyFilter(
  filters: FilterDefinitionItem[],
  selectedCompanyIds: string | string[] | null | undefined
): FilterDefinitionItem[] {
  return updateDependentSelectionFilter(filters, 'propertyIds', toIds(selectedCompanyIds));
}

export function updatePropertyDependentBuildingFilter(
  filters: FilterDefinitionItem[],
  selectedPropertyIds: string | string[] | null | undefined,
  selectedCompanyIds: string | string[] | null | undefined
): FilterDefinitionItem[] {
  return updateDependentSelectionFilter(filters, 'buildingIds', {
    companyIds: toIds(selectedCompanyIds),
    propertyIds: toIds(selectedPropertyIds),
  });
}

export function updateBuildingDependentEntranceFilter(
  filters: FilterDefinitionItem[],
  selectedBuildingIds: string | string[] | null | undefined,
  selectedPropertyIds: string | string[] | null | undefined,
  selectedCompanyIds: string | string[] | null | undefined
): FilterDefinitionItem[] {
  return updateDependentSelectionFilter(filters, 'entranceIds', {
    companyIds: toIds(selectedCompanyIds),
    propertyIds: toIds(selectedPropertyIds),
    buildingIds: toIds(selectedBuildingIds),
  });
}

/**
 * Returns whether applying a new parent scope can make the current child selection invalid.
 * Expanding an existing scope, or removing the scope entirely, keeps the child selection valid.
 */
export function shouldClearDependentSelection(
  filters: FilterDefinitionItem[],
  dependentFilterName: string,
  selectedParentIds: string | string[] | null | undefined,
  scopeKey?: string
): boolean {
  const dependentFilter = filters?.find(filter => filter.name === dependentFilterName);
  if (!dependentFilter) {
    return false;
  }

  const currentParentIds = toIds(scopeKey && isScope(dependentFilter.serviceOptions) ? dependentFilter.serviceOptions[scopeKey] : dependentFilter.serviceOptions);
  const parentIds = toIds(selectedParentIds);
  return parentIds.length > 0 && (currentParentIds.length === 0 || currentParentIds.some(parentId => !parentIds.includes(parentId)));
}

function updateDependentSelectionFilter(
  filters: FilterDefinitionItem[],
  dependentFilterName: string,
  serviceOptions: string[] | Record<string, string[]>
): FilterDefinitionItem[] {
  const dependentFilter = filters?.find(filter => filter.name === dependentFilterName);
  if (!dependentFilter) {
    return filters;
  }

  const currentOptions = dependentFilter.serviceOptions;

  if (sameServiceOptions(currentOptions, serviceOptions)) {
    return filters;
  }

  dependentFilter.serviceOptions = Array.isArray(serviceOptions) && serviceOptions.length === 0 ? undefined : serviceOptions;
  return [...filters];
}

function sameServiceOptions(left: unknown, right: string[] | Record<string, string[]>): boolean {
  if (Array.isArray(right)) {
    return sameIds(toIds(left), right);
  }

  if (!isScope(left)) {
    return false;
  }

  return Object.keys(right).every(key => sameIds(toIds(left[key]), right[key]))
    && Object.keys(left).every(key => key in right);
}

function sameIds(left: string[], right: string[]): boolean {
  return left.length === right.length && left.every(id => right.includes(id));
}

function isScope(value: unknown): value is Record<string, string[] | undefined> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function toIds(value: unknown): string[] {
  return (Array.isArray(value) ? value : value ? [value] : []).filter(Boolean);
}
